// CRM Pipeline page — kanban-style buckets of customers by lead_status plus
// follow-ups due today / overdue. Drag-drop is intentionally deferred — we
// surface a status dropdown on every card to keep the experience accessible
// (and so the kanban works without a touch-DnD library).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Funnel, Clock, CheckCircle, Trash, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, POUND } from "../lib/api";

const STATUSES = ["new", "contacted", "qualified", "quoted", "won", "lost"];

const STATUS_META = {
  new:        { label: "New",        accent: "border-t-slate-400",   tone: "text-slate-700"   },
  contacted:  { label: "Contacted",  accent: "border-t-blue-400",    tone: "text-blue-700"    },
  qualified:  { label: "Qualified",  accent: "border-t-indigo-400",  tone: "text-indigo-700"  },
  quoted:     { label: "Quoted",     accent: "border-t-amber-400",   tone: "text-amber-700"   },
  won:        { label: "Won",        accent: "border-t-emerald-400", tone: "text-emerald-700" },
  lost:       { label: "Lost",       accent: "border-t-rose-400",    tone: "text-rose-700"    },
};

const FOLLOWUP_TYPES = [
  { id: "call",       label: "Call"        },
  { id: "email",      label: "Email"       },
  { id: "site_visit", label: "Site visit"  },
  { id: "quote_send", label: "Send quote"  },
  { id: "chase",      label: "Chase"       },
  { id: "review",     label: "Review"      },
  { id: "other",      label: "Other"       },
];

const fmtDue = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const now = new Date();
  const days = Math.round((d - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days <= 7) return `in ${days}d`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

function FollowupDialog({ open, customers, onClose, onSaved }) {
  const [form, setForm] = useState({ customer_id: "", type: "call", title: "", notes: "", due_at: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // default to one day from now at 09:00
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      setForm({
        customer_id: customers[0]?.id || "",
        type: "call",
        title: "",
        notes: "",
        due_at: d.toISOString().slice(0, 16),
      });
    }
  }, [open, customers]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    if (!form.due_at) { toast.error("Pick a due date"); return; }
    setSaving(true);
    try {
      const due = new Date(form.due_at).toISOString();
      const { data } = await api.post("/followups", { ...form, due_at: due });
      toast.success("Follow-up scheduled");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to schedule");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card-elev w-full max-w-md p-6"
        data-testid="followup-dialog"
      >
        <div className="overline">New follow-up</div>
        <h3 className="font-display text-2xl font-bold mt-1">Schedule a reminder</h3>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <label className="block col-span-2">
            <span className="overline">Customer</span>
            <select className="rqp mt-1.5" value={form.customer_id} onChange={set("customer_id")} data-testid="followup-customer-select" required>
              <option value="">—</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.postcode ? ` (${c.postcode})` : ""}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="overline">Type</span>
            <select className="rqp mt-1.5" value={form.type} onChange={set("type")} data-testid="followup-type-select">
              {FOLLOWUP_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="overline">Due</span>
            <input type="datetime-local" className="rqp mt-1.5" value={form.due_at} onChange={set("due_at")} data-testid="followup-due-input" required />
          </label>
          <label className="block col-span-2">
            <span className="overline">Title</span>
            <input className="rqp mt-1.5" value={form.title} onChange={set("title")} placeholder="Chase quote on Acacia Rd" data-testid="followup-title-input" />
          </label>
          <label className="block col-span-2">
            <span className="overline">Notes</span>
            <textarea rows={2} className="rqp mt-1.5" value={form.notes} onChange={set("notes")} data-testid="followup-notes-input" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="followup-save-button">{saving ? "Saving…" : "Schedule"}</button>
        </div>
      </form>
    </div>
  );
}

function PipelineCard({ entry, onStatusChange }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm p-3 hover:border-slate-400 transition-colors" data-testid={`pipeline-card-${entry.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm truncate">{entry.name || "Unnamed lead"}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-data mt-0.5">
            {entry.postcode || "—"}{entry.source ? ` · ${entry.source}` : ""}
          </div>
        </div>
        <Link to={`/quotes/new?customer=${entry.id}`} className="text-slate-400 hover:text-[var(--rqp-orange)]" title="New quote" data-testid={`pipeline-card-quote-${entry.id}`}>
          <ArrowUpRight size={14} weight="bold" />
        </Link>
      </div>
      <div className="text-xs font-mono-data text-slate-600 mt-2">
        Forecast <strong className="text-slate-900">{POUND(entry.value || 0)}</strong>
      </div>
      <div className="flex items-center justify-between mt-2 gap-2">
        <select
          value={entry.lead_status_current || ""}
          onChange={(e) => onStatusChange(entry.id, e.target.value)}
          className="text-[10px] uppercase tracking-wider font-bold border border-slate-300 rounded-sm px-1.5 py-1 bg-white hover:bg-slate-50"
          data-testid={`pipeline-card-status-${entry.id}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        {entry.next_followup_at && (
          <span className="text-[10px] text-amber-700 font-mono-data" data-testid={`pipeline-card-followup-${entry.id}`}>
            <Clock size={10} weight="bold" className="inline mr-0.5 mb-0.5" /> {fmtDue(entry.next_followup_at)}
          </span>
        )}
      </div>
    </div>
  );
}

function FollowupRow({ f, customer, onComplete, onDelete }) {
  const overdue = f.due_at && new Date(f.due_at) < new Date();
  return (
    <li
      className={`p-3 rounded-sm border ${overdue ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}
      data-testid={`followup-row-${f.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="font-semibold text-sm truncate">{f.title || `${(FOLLOWUP_TYPES.find((t) => t.id === f.type) || {}).label || f.type} — ${customer?.name || ""}`}</div>
            {f.auto && (
              <span
                className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border bg-violet-50 text-violet-700 border-violet-200"
                data-testid={`followup-auto-${f.id}`}
                title={`Auto-scheduled when quote moved to ${f.auto_status}`}
              >
                Auto
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {customer?.name || "—"}{customer?.postcode ? ` · ${customer.postcode}` : ""}
          </div>
          {f.notes && <div className="text-xs text-slate-600 mt-1.5">{f.notes}</div>}
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-1 rounded-sm border ${overdue ? "bg-rose-100 text-rose-700 border-rose-300" : "bg-slate-100 text-slate-700 border-slate-300"}`}>
            {fmtDue(f.due_at)}
          </span>
          <button
            onClick={() => onComplete(f.id)}
            className="btn-secondary !px-2 !py-1 text-emerald-700 hover:bg-emerald-50"
            title="Mark complete"
            data-testid={`followup-complete-${f.id}`}
          >
            <CheckCircle size={14} weight="bold" />
          </button>
          <button
            onClick={() => onDelete(f.id)}
            className="btn-secondary !px-2 !py-1 text-red-600 hover:bg-red-50"
            title="Delete"
            data-testid={`followup-delete-${f.id}`}
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </div>
    </li>
  );
}

export default function Pipeline() {
  const [pipeline, setPipeline] = useState({ buckets: [], total_customers: 0, total_forecast: 0 });
  const [followups, setFollowups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [pl, fu, cs] = await Promise.all([
        api.get("/crm/pipeline"),
        api.get("/followups?status=pending&limit=200"),
        api.get("/customers"),
      ]);
      setPipeline(pl.data);
      setFollowups(fu.data);
      setCustomers(Array.isArray(cs.data) ? cs.data : (cs.data.items || []));
    } catch (e) {
      toast.error("Failed to load pipeline");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const customerById = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c])),
    [customers],
  );

  const enrichedBuckets = useMemo(() => {
    return STATUSES.map((s) => {
      const b = pipeline.buckets.find((x) => x.status === s) || { status: s, count: 0, value: 0, customers: [] };
      return {
        ...b,
        customers: (b.customers || []).map((entry) => ({
          ...entry,
          lead_status_current: s,
        })),
      };
    });
  }, [pipeline]);

  const updateLeadStatus = async (cid, newStatus) => {
    const cust = customers.find((c) => c.id === cid);
    if (!cust) return;
    try {
      const { data } = await api.put(`/customers/${cid}`, { ...cust, lead_status: newStatus });
      setCustomers((prev) => prev.map((c) => (c.id === cid ? data : c)));
      // Optimistically move on the board
      setPipeline((prev) => {
        const buckets = prev.buckets.map((b) => ({ ...b, customers: [...(b.customers || [])] }));
        let moved = null;
        for (const b of buckets) {
          const idx = b.customers.findIndex((c) => c.id === cid);
          if (idx >= 0) {
            moved = b.customers[idx];
            b.customers.splice(idx, 1);
            b.count = Math.max(0, b.count - 1);
            b.value = Math.max(0, (b.value || 0) - (moved.value || 0));
            break;
          }
        }
        if (moved) {
          const target = buckets.find((b) => b.status === newStatus) || (() => {
            const nb = { status: newStatus, count: 0, value: 0, customers: [] };
            buckets.push(nb);
            return nb;
          })();
          target.customers.unshift({ ...moved, lead_status_current: newStatus });
          target.count += 1;
          target.value += moved.value || 0;
        }
        return { ...prev, buckets };
      });
      toast.success(`Moved to ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    }
  };

  const completeFollowup = async (fid) => {
    try {
      await api.patch(`/followups/${fid}`, { completed: true });
      setFollowups((prev) => prev.filter((f) => f.id !== fid));
      toast.success("Follow-up done");
      // refresh pipeline so next_followup_at moves on
      const { data } = await api.get("/crm/pipeline");
      setPipeline(data);
    } catch (e) { toast.error("Failed to complete"); }
  };

  const deleteFollowup = async (fid) => {
    if (!window.confirm("Delete this follow-up?")) return;
    try {
      await api.delete(`/followups/${fid}`);
      setFollowups((prev) => prev.filter((f) => f.id !== fid));
      const { data } = await api.get("/crm/pipeline");
      setPipeline(data);
    } catch (e) { toast.error("Failed to delete"); }
  };

  const overdueCount = followups.filter((f) => f.due_at && new Date(f.due_at) < new Date()).length;

  return (
    <div className="p-6 lg:p-10 max-w-[1500px]">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <span className="hero-rule" />
          <div className="overline">CRM</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">Lead pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Track every lead from first contact to a signed job. Drag a card via the status dropdown — every move is auto-saved.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setDialogOpen(true)} className="btn-primary flex items-center gap-2" data-testid="pipeline-new-followup">
            <Plus size={14} weight="bold" /> New follow-up
          </button>
          <Link to="/customers" className="btn-secondary flex items-center gap-2" data-testid="pipeline-new-customer">
            <Funnel size={14} weight="bold" /> Manage customers
          </Link>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-elev p-4" data-testid="pipeline-stat-leads">
          <div className="overline">Total leads</div>
          <div className="font-display text-3xl font-bold mt-1">{pipeline.total_customers}</div>
        </div>
        <div className="card-elev p-4" data-testid="pipeline-stat-forecast">
          <div className="overline">Pipeline forecast</div>
          <div className="font-display text-3xl font-bold mt-1 text-[var(--rqp-orange)]">{POUND(pipeline.total_forecast || 0)}</div>
        </div>
        <div className="card-elev p-4" data-testid="pipeline-stat-followups">
          <div className="overline">Pending follow-ups</div>
          <div className="font-display text-3xl font-bold mt-1">{followups.length}</div>
        </div>
        <div className={`card-elev p-4 ${overdueCount ? "border-rose-300" : ""}`} data-testid="pipeline-stat-overdue">
          <div className="overline">Overdue</div>
          <div className={`font-display text-3xl font-bold mt-1 ${overdueCount ? "text-rose-600" : "text-slate-400"}`}>{overdueCount}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="pipeline-loading">Loading pipeline…</div>
      ) : (
        <>
          {/* Kanban */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="pipeline-board">
            {enrichedBuckets.map((b) => {
              const meta = STATUS_META[b.status] || { label: b.status, accent: "border-t-slate-300" };
              return (
                <div
                  key={b.status}
                  className={`card-elev p-3 border-t-4 ${meta.accent}`}
                  data-testid={`pipeline-bucket-${b.status}`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className={`font-display font-bold text-sm uppercase tracking-wider ${meta.tone}`}>{meta.label}</div>
                    <div className="font-mono-data text-xs text-slate-500">{b.count}</div>
                  </div>
                  <div className="font-mono-data text-xs text-slate-600 mt-0.5">{POUND(b.value || 0)}</div>
                  <div className="mt-3 space-y-2">
                    {b.customers.length === 0 ? (
                      <div className="text-[11px] text-slate-400 italic text-center py-4">No leads</div>
                    ) : (
                      b.customers.map((entry) => (
                        <PipelineCard
                          key={entry.id}
                          entry={entry}
                          onStatusChange={updateLeadStatus}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Follow-ups */}
          <div className="mt-8 card-elev p-5" data-testid="pipeline-followups">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <span className="overline">Reminders</span>
                <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Pending follow-ups</h2>
              </div>
              <button onClick={() => setDialogOpen(true)} className="btn-secondary flex items-center gap-2 text-xs" data-testid="pipeline-followup-add-inline">
                <Plus size={14} weight="bold" /> Schedule
              </button>
            </div>
            {followups.length === 0 ? (
              <div className="text-sm text-slate-500 italic mt-4" data-testid="pipeline-no-followups">
                Nothing scheduled. Use “New follow-up” to add a chase, callback or site visit.
              </div>
            ) : (
              <ul className="mt-4 space-y-2">
                {followups.map((f) => (
                  <FollowupRow
                    key={f.id}
                    f={f}
                    customer={customerById[f.customer_id]}
                    onComplete={completeFollowup}
                    onDelete={deleteFollowup}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <FollowupDialog
        open={dialogOpen}
        customers={customers}
        onClose={() => setDialogOpen(false)}
        onSaved={(saved) => {
          setFollowups((prev) => [saved, ...prev].sort((a, b) => (a.due_at || "").localeCompare(b.due_at || "")));
          // Refresh pipeline so card shows the new next_followup_at
          api.get("/crm/pipeline").then((r) => setPipeline(r.data)).catch(() => {});
        }}
      />
    </div>
  );
}
